import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Row, Col, Divider, Typography, Button } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Modal for editing invoice/receipt details before PDF generation
 * Allows users to customize company info, billing details, and transaction items
 */
const InvoiceReceiptEditModal = ({
  visible,
  onCancel,
  onGenerate,
  transaction,
  documentType, // 'invoice', 'receipt', or 'both'
  timezone,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Initialize form with transaction data when modal opens
  useEffect(() => {
    if (visible && transaction) {
      form.setFieldsValue({
        // Company/From details
        companyName: 'KiteGateway',
        companyAddress: 'Payment Gateway Services',
        companyEmail: 'support@kitegateway.com',

        // Bill To details - prefilled with customer information from transaction
        billToName: transaction.customer_name || transaction.business_name || transaction.billing?.name || 'Customer',
        billToAddress: transaction.billing?.address || transaction.customer_address || '',
        billToEmail: transaction.customer_email || transaction.billing?.email || transaction.email || '',
        billToPhone: transaction.customer_phone || transaction.customer_mobile || transaction.billing?.phone || transaction.phone_number || '',

        // Transaction details
        merchantReference: transaction.merchant_reference || '',
        description: transaction.narration || `${transaction.transaction_type} Transaction`,
        amount: transaction.transaction_amount,
        currency: transaction.transaction_currency,

        // Additional info
        notes: '',
      });
    }
  }, [visible, transaction, form]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // Prepare the data for PDF generation
      const customizedData = {
        ...transaction,
        // Company info
        company: {
          name: values.companyName,
          address: values.companyAddress,
          email: values.companyEmail,
        },
        // Bill To info
        billTo: {
          name: values.billToName,
          address: values.billToAddress,
          email: values.billToEmail,
          phone: values.billToPhone,
        },
        // Transaction item
        item: {
          description: values.description,
          amount: values.amount,
          currency: values.currency,
          merchantReference: values.merchantReference,
        },
        // Additional
        notes: values.notes,
      };

      // Call the parent's onGenerate function with customized data
      await onGenerate(customizedData);
      setLoading(false);
    } catch (error) {
      console.error('Form validation failed:', error);
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (documentType) {
      case 'invoice':
        return 'Edit Invoice Details';
      case 'receipt':
        return 'Edit Receipt Details';
      case 'both':
        return 'Edit Invoice & Receipt Details';
      default:
        return 'Edit Document Details';
    }
  };

  const getButtonText = () => {
    switch (documentType) {
      case 'invoice':
        return 'Generate Invoice PDF';
      case 'receipt':
        return 'Generate Receipt PDF';
      case 'both':
        return 'Generate Invoice & Receipt PDF';
      default:
        return 'Generate PDF';
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={getModalTitle()}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<FilePdfOutlined />}
          loading={loading}
          onClick={handleGenerate}
        >
          {getButtonText()}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        name="invoiceReceiptEdit"
      >
        {/* Company/From Information */}
        <Divider orientation="left">
          <Title level={5}>From (Company Details)</Title>
        </Divider>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="companyName"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="Enter company name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="companyAddress"
              label="Company Address"
              rules={[{ required: true, message: 'Please enter company address' }]}
            >
              <TextArea
                rows={2}
                placeholder="Enter company address"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="companyEmail"
              label="Company Email"
              rules={[
                { required: true, message: 'Please enter company email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter company email" />
            </Form.Item>
          </Col>
        </Row>

        {/* Bill To Information */}
        <Divider orientation="left">
          <Title level={5}>Bill To (Customer Details)</Title>
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="billToName"
              label="Customer Name"
              rules={[{ required: true, message: 'Please enter customer name' }]}
            >
              <Input placeholder="Enter customer name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="billToEmail"
              label="Customer Email"
              rules={[
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter customer email" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="billToAddress"
              label="Customer Address"
            >
              <TextArea
                rows={2}
                placeholder="Enter customer address"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="billToPhone"
              label="Customer Phone"
            >
              <Input placeholder="Enter customer phone" />
            </Form.Item>
          </Col>
        </Row>

        {/* Transaction Details */}
        <Divider orientation="left">
          <Title level={5}>Transaction Item</Title>
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="merchantReference"
              label="Merchant Reference"
            >
              <Input placeholder="Enter merchant reference" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Transaction Reference">
              <Text strong>{transaction?.kitegateway_reference || 'N/A'}</Text>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label="Item Description"
              rules={[{ required: true, message: 'Please enter item description' }]}
            >
              <TextArea
                rows={2}
                placeholder="Enter item/service description"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: 'Please enter amount' }]}
            >
              <Input
                type="number"
                placeholder="Enter amount"
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label="Currency"
              rules={[{ required: true, message: 'Please enter currency' }]}
            >
              <Input placeholder="Enter currency" disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">
          <Title level={5}>Additional Information</Title>
        </Divider>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="notes"
              label="Notes (Optional)"
            >
              <TextArea
                rows={3}
                placeholder="Add any additional notes or terms & conditions"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Transaction Info (Read-only) */}
        <Divider orientation="left">
          <Title level={5}>Transaction Information</Title>
        </Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Text type="secondary">Date:</Text>
            <br />
            <Text strong>
              {transaction?.created_at
                ? moment(transaction.created_at).utc().add('hours', timezone?.offset || 0).format('DD/MM/YYYY h:mm:ss a')
                : 'N/A'
              }
            </Text>
          </Col>
          <Col span={8}>
            <Text type="secondary">Type:</Text>
            <br />
            <Text strong>{transaction?.transaction_type || 'N/A'}</Text>
          </Col>
          <Col span={8}>
            <Text type="secondary">Status:</Text>
            <br />
            <Text strong>{transaction?.transaction_status || 'N/A'}</Text>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

InvoiceReceiptEditModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  transaction: PropTypes.object,
  documentType: PropTypes.oneOf(['invoice', 'receipt', 'both']).isRequired,
  timezone: PropTypes.object,
};

InvoiceReceiptEditModal.defaultProps = {
  transaction: null,
  timezone: { offset: 0 },
};

export default InvoiceReceiptEditModal;
